import { type ComponentProps, type MouseEvent, type Ref, useMemo } from "react";
import { type VariantProps } from "class-variance-authority";
import { Slot, Slottable } from "@radix-ui/react-slot";

import { cn } from "../../lib/utils";
import { Spinner } from "../Spinner";
import { Tooltip } from "../Tooltip";
import { buttonVariants, type ButtonColor } from "./styles";
import { ButtonGroup } from "./ButtonGroup";

type ButtonStyleProps = Omit<VariantProps<typeof buttonVariants>, "iconOnly">;

export interface ButtonProps extends ComponentProps<"button">, ButtonStyleProps {
  ref?: Ref<HTMLButtonElement>;
  /**
   * Shape / emphasis of the button. `soft` is a quiet always-visible tinted fill — one step down
   * from `solid`, but unlike `ghost`/`outline` it still reads as a filled control at rest. Reach
   * for it where a row of solid buttons would be too loud, or where the button sits on a tinted
   * surface of its own.
   *
   * `inline` is chrome-free: ink only, with no fill, border, shadow, or hover fill. Use it for a
   * control that sits inside a surface that already provides the chrome (a toolbar, a header rail);
   * use `ghost` instead when a standalone button needs hover feedback.
   *
   * `raised` is a neutral surface one step above its container, with no hover change. It marks a
   * selected item (the active nav link, a selected segment) rather than an action.
   * @default "solid"
   */
  variant?: "solid" | "soft" | "outline" | "ghost" | "link" | "inline" | "raised";
  /**
   * Corner shape: `default` is the token radius, `round` a larger 12px radius, and `pill` fully
   * rounded (a circle when `iconOnly`). Inside a `Button.Group` that sets `radius`, the group's
   * value wins.
   * @default "default"
   */
  radius?: "default" | "round" | "pill";
  /**
   * Intent color, applied on top of the chosen `variant`. `success`/`warning`/`info` are the status
   * hues, and `ai` the assistant hue. `white` is a literal, theme-fixed white for buttons on a
   * dark/colored/photo surface — it does not invert in dark mode.
   * @default "primary"
   */
  color?: ButtonColor;
  /**
   * Dimensional size.
   * @default "md"
   */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * Renders a square, icon-only button (no horizontal padding). Composes with `size`. Provide an
   * accessible name via `aria-label` or `hoverLabel` when the button has no visible text.
   * @default false
   */
  iconOnly?: boolean;
  /** Tooltip text shown on hover/focus; wraps the button in a `Tooltip`. */
  hoverLabel?: string;
  /** When true, shows a leading spinner and disables the button. */
  loading?: boolean;
  /**
   * Merge props onto the child element (Radix `Slot`) instead of rendering a `<button>`. This is
   * how a Button becomes a link: pass the app's router `Link` as the only child. With `disabled` or
   * `loading`, the child gets `aria-disabled`, leaves the tab order, and swallows clicks, since an
   * anchor ignores the native `disabled` attribute.
   */
  asChild?: boolean;
}

// An anchor has no native disabled state. Stopping the click in the capture phase keeps it from
// reaching the anchor's own handlers (a router Link navigates in its onClick), not just the default.
const suppressActivation = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

const DISABLED_SLOT_PROPS = {
  "aria-disabled": true,
  tabIndex: -1,
  onClickCapture: suppressActivation,
};

/**
 * The primary interactive control. Three orthogonal axes — `variant` (shape), `color` (intent),
 * and `size` (dimension) — compose freely; `iconOnly` renders a square icon button.
 *
 * @example
 * ```tsx
 * <Button>Save</Button>                                  // solid primary, md
 * <Button color="destructive">Delete</Button>            // solid red
 * <Button variant="outline" color="destructive">Remove</Button>
 * <Button variant="soft" color="ai">Continue</Button>     // quiet tinted fill, not a solid
 * <Button variant="ghost" size="sm" iconOnly hoverLabel="Edit"><Pencil /></Button>
 * <Button color="white">On a dark surface</Button>  // theme-fixed white, never inverts
 * <Button loading>Saving…</Button>
 * ```
 */
export const ButtonRoot = ({
  className,
  variant = "solid",
  color = "primary",
  size = "md",
  radius = "default",
  iconOnly = false,
  hoverLabel,
  loading,
  asChild,
  disabled,
  children,
  ref,
  ...props
}: ButtonProps) => {
  const Comp = asChild ? Slot : "button";
  const isDisabled = loading || disabled;
  const disabledSlotProps = asChild && isDisabled ? DISABLED_SLOT_PROPS : undefined;
  const resolvedProps = useMemo<typeof props>(
    () => (Comp === "button" ? { type: "button", ...props } : props),
    [Comp, props]
  );

  // Solid buttons have a filled colored background, so the spinner needs an on-color (light) tone;
  // outline/ghost/link buttons are transparent with colored text, so the spinner tracks the intent.
  // `soft` sides with the latter despite painting a background: its fill is a pale tint written in
  // the role's own saturated ink, so an on-color (light) spinner would wash out against it.
  // `secondary` and `muted` are the exceptions: even their solid fills are light surfaces, so a `white`
  // spinner would wash out — they always track their own (dark-ink) intent tone regardless of variant.
  // `white` solid is the mirror image: a white spinner on a white fill would be invisible, so it
  // takes the dark ink the fill is paired with. `raised` is a light surface too, so it does the same.
  // The status hues have no ring role of their own, so off a filled surface they fall back to the
  // neutral muted tone rather than their own (undefined) intent.
  const isStatusHue = color === "success" || color === "warning" || color === "info";
  const tracksOwnTone = color === "muted" || color === "secondary";
  const onFilledSurface = variant === "solid" && !tracksOwnTone;
  const onLightSurface = variant === "solid" || variant === "raised";
  const spinnerColor =
    color === "white" && onLightSurface ? "primary" : onFilledSurface ? "white" : isStatusHue ? "muted" : color;

  const buttonEl = (
    <Comp
      ref={ref}
      data-slot="button"
      data-loading={loading ? "true" : undefined}
      className={cn(
        buttonVariants({ variant, color, size, radius, iconOnly }),
        hoverLabel && disabled && "disabled:pointer-events-auto",
        className
      )}
      {...resolvedProps}
      // An icon-only button has no visible text to name it, so `hoverLabel` doubles as its
      // accessible name. Buttons with visible text keep that text as their name — labelling them
      // with `hoverLabel` would hide the visible label from assistive tech.
      aria-label={props["aria-label"] ?? (iconOnly ? hoverLabel : undefined)}
      disabled={asChild ? undefined : isDisabled}
      {...disabledSlotProps}
    >
      {!!loading && <Spinner size="xs" color={spinnerColor} />}
      <Slottable>{children}</Slottable>
    </Comp>
  );

  if (hoverLabel) {
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>{buttonEl}</Tooltip.Trigger>
        <Tooltip.Content>{hoverLabel}</Tooltip.Content>
      </Tooltip>
    );
  }

  return buttonEl;
};

export const Button = Object.assign(ButtonRoot, { Group: ButtonGroup });
